import React, { Fragment, useContext } from "react";
import * as Sentry from "@sentry/browser";
import { Router, Route, Redirect } from "react-router-dom";
import { AppContext, AppContextProvider } from "./components/Utils/AppContext";
import { logoutUser } from "./components/Utils/User";
import createHistory from "history/createBrowserHistory";
import ReactGA from "react-ga";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "minireset.css";
import "./assets/css/global.css";

import Home from "./components/Pages/Home";
import Login from "./components/Pages/Login";
import ResetPassword from "./components/Pages/ResetPassword";
import Companies from "./components/Pages/Companies";
import CompanyDetails from "./components/Pages/CompanyDetails";
import Users from "./components/Pages/Users";
import Drivers from "./components/Pages/Drivers";
import Fleets from "./components/Pages/Fleets";
import UserAccount from "./components/Pages/UserAccount";
import FleetDetails from "./components/Pages/FleetDetails";

toast.configure({
  autoClose: 3000,
  draggable: false
});

ReactGA.initialize("UA-91235010-3");

const history = createHistory();
history.listen((location, action) => {
  ReactGA.set({ page: location.pathname });
  ReactGA.pageview(location.pathname);
});

const AppRouter = () => {
  Sentry.init({
    dsn: "https://bfa647cccfa04e039af16b337a72e9a8@sentry.io/1440141"
  });

  const context = useContext(AppContext);
  const { state } = context;

  if (state.loading) return <p>loading...</p>;

  return (
    <Router history={history}>
      <Fragment>
        <Route
          exact
          path="/"
          component={() => {
            if (state.authenticated) {
              // eslint-disable-next-line default-case
              switch (state.user.role) {
                case "super":
                  // console.log("user is super");
                  return <Companies />;
                case "company-manager":
                  // console.log("user is company manager");
                  return <Redirect to="/fleets" />;
                case "fleet-manager":
                  // console.log("user is fleet manager");
                  return <Redirect to="/fleets" />;
                default:
                // console.log("user has no role");
              }
            } else {
              return <Redirect to="/login" />;
            }
          }}
        />

        <Route exact path="/login" component={() => (!state.authenticated ? <Login /> : <Redirect to="/" />)} />

        <Route
          exact
          path="/forgot-password"
          component={() => (!state.authenticated ? <ResetPassword /> : <Redirect to="/" />)}
        />

        <Route
          exact
          path="/logout"
          component={() => {
            if (!state.authenticated) {
              return <Redirect to="/" />;
            }
            return logoutUser();
          }}
        />

        <Route
          exact
          path="/company"
          component={props =>
            !state.authenticated ? (
              <Redirect to="/" />
            ) : (
              // eslint-disable-next-line react/prop-types
              <CompanyDetails isHome />
            )
          }
        />

        <Route
          exact
          path="/company/:companyId"
          component={props =>
            !state.authenticated ? (
              <Redirect to="/" />
            ) : (
              // eslint-disable-next-line react/prop-types
              <CompanyDetails route={props.match} />
            )
          }
        />

        <Route exact path="/managers" component={() => (!state.authenticated ? <Redirect to="/" /> : <Users />)} />

        <Route
          exact
          path="/user/:userId"
          component={props => (!state.authenticated ? <Redirect to="/" /> : <UserAccount route={props.match} />)}
        />

        <Route exact path="/drivers" component={() => (!state.authenticated ? <Redirect to="/" /> : <Drivers />)} />

        <Route exact path="/fleets" component={() => (!state.authenticated ? <Redirect to="/" /> : <Fleets />)} />

        <Route
          exact
          path="/fleet/:fleetId"
          component={props =>
            !state.authenticated ? <Redirect to="/" /> : <FleetDetails location={props.location} match={props.match} />
          }
        />

        <Route
          exact
          path="/:companyId/fleet/:fleetId"
          component={props =>
            !state.authenticated && state.user.role !== "super" ? (
              <Redirect to="/" />
            ) : (
              <FleetDetails location={props.location} match={props.match} isSuper />
            )
          }
        />
      </Fragment>
    </Router>
  );
};

const App = () => (
  <AppContextProvider>
    <AppRouter />
  </AppContextProvider>
);

export default App;
